import { useState, useCallback } from 'react';
import styles from './ColourPalette.module.css';

function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      default: h = ((r - g) / d + 4) / 6;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function getLuminance(r, g, b) {
  return [r, g, b].reduce((sum, c, i) => {
    c /= 255;
    c = c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    return sum + c * [0.2126, 0.7152, 0.0722][i];
  }, 0);
}

function contrastRatio(hex, bgLum) {
  const { r, g, b } = hexToRgb(hex);
  const lum = getLuminance(r, g, b);
  return bgLum > lum
    ? (bgLum + 0.05) / (lum + 0.05)
    : (lum + 0.05) / (bgLum + 0.05);
}


function isValidHex(hex) {
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

function generatePalette(hex) {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  return [
    { name: 'Primary', hex, role: 'Main brand colour' },
    { name: 'Complementary', hex: hslToHex((h + 180) % 360, s, l), role: 'High-contrast accent' },
    { name: 'Analogous', hex: hslToHex((h + 30) % 360, s, l), role: 'Warm harmony' },
    { name: 'Split', hex: hslToHex((h - 30 + 360) % 360, s, l), role: 'Cool harmony' },
    { name: 'Triadic', hex: hslToHex((h + 120) % 360, s, l), role: 'Vibrant third' },
  ];
}

const DEFAULT = '#2D545E';

const ColourPalette = () => {
  const [input, setInput] = useState(DEFAULT);
  const [hex, setHex] = useState(DEFAULT);
  const [copied, setCopied] = useState(false);

  const palette = generatePalette(hex);

  const handleInput = useCallback(e => {
    const val = e.target.value;
    setInput(val);
    const normalized = val.startsWith('#') ? val : `#${val}`;
    if (isValidHex(normalized)) setHex(normalized);
  }, []);

  const handlePicker = useCallback(e => {
    const val = e.target.value;
    setInput(val);
    setHex(val);
  }, []);

  const copyCSS = useCallback(() => {
    const vars = palette.map(p =>
      `  --color-${p.name.toLowerCase()}: ${p.hex};`
    ).join('\n');
    navigator.clipboard.writeText(`:root {\n${vars}\n}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [palette]);

  const white = 1;
  const black = 0;

  return (
    <div className={styles.wrap}>
      {/* Input row */}
      <div className={styles.inputRow}>
        <label className={styles.label} htmlFor="cp-hex">Brand colour</label>
        <div className={styles.inputGroup}>
          <input
            type="color"
            className={styles.colorPicker}
            value={hex}
            onChange={handlePicker}
            aria-label="Colour picker"
          />
          <input
            id="cp-hex"
            type="text"
            className={styles.hexInput}
            value={input}
            onChange={handleInput}
            placeholder="#00BCD4"
            maxLength={7}
            spellCheck={false}
          />
        </div>
      </div>

      {/* Swatches */}
      <div className={styles.swatches}>
        {palette.map(swatch => {
          const wRatio = contrastRatio(swatch.hex, white);
          const bRatio = contrastRatio(swatch.hex, black);
          const useWhiteText = wRatio >= bRatio;
          const bestRatio = Math.max(wRatio, bRatio);
          const level = bestRatio >= 7 ? 'AAA' : bestRatio >= 4.5 ? 'AA' : 'AA Large';
          const badgeStyle = bestRatio >= 4.5 ? styles.wcagPass : styles.wcagWarn;

          return (
            <div key={swatch.name} className={styles.swatch}>
              <div
                className={styles.swatchColor}
                style={{ background: swatch.hex }}
              >
                <span
                  className={styles.swatchHex}
                  style={{ color: useWhiteText ? '#fff' : '#000' }}
                >
                  {swatch.hex.toUpperCase()}
                </span>
              </div>
              <div className={styles.swatchMeta}>
                <span className={styles.swatchName}>{swatch.name}</span>
                <span className={styles.swatchRole}>{swatch.role}</span>
                <div className={styles.wcagRow}>
                  <span className={`${styles.wcagBadge} ${badgeStyle}`}>
                    {useWhiteText ? 'White' : 'Black'} text · {bestRatio.toFixed(1)}:1 · {level}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Live preview */}
      <div className={styles.previewSection}>
        <p className={styles.previewLabel}>Live preview</p>
        <div className={styles.preview} style={{ background: palette[0].hex }}>
          <div className={styles.previewHeader} style={{ background: `${palette[0].hex}dd` }}>
            <div className={styles.previewLogo} />
            <div className={styles.previewNav}>
              {['Home', 'About', 'Contact'].map(l => (
                <div key={l} className={styles.previewNavItem} style={{ background: 'rgba(255,255,255,0.25)' }} />
              ))}
            </div>
          </div>
          <div className={styles.previewHero}>
            <div className={styles.previewHeadline} style={{ background: 'rgba(255,255,255,0.9)' }} />
            <div className={styles.previewSub} style={{ background: 'rgba(255,255,255,0.6)' }} />
            <div className={styles.previewBtn} style={{ background: palette[1].hex }} />
          </div>
          <div className={styles.previewCards}>
            {palette.slice(1, 4).map((p, i) => (
              <div key={i} className={styles.previewCard} style={{ background: p.hex + '33', borderColor: p.hex + '66' }}>
                <div className={styles.previewCardIcon} style={{ background: p.hex }} />
                <div className={styles.previewCardLine} style={{ background: p.hex + '88' }} />
                <div className={styles.previewCardLine} style={{ background: p.hex + '55', width: '60%' }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Export */}
      <div className={styles.footer}>
        <button className={styles.copyBtn} onClick={copyCSS}>
          {copied ? '✓ Copied to clipboard' : 'Copy as CSS variables'}
        </button>
      </div>
    </div>
  );
};

export default ColourPalette;
